import { ReactElement, useMemo } from "react";

import { Box, Tooltip, Typography } from "@mui/material";
import {
  GridActionsCellItem,
  GridActionsCellItemProps,
  GridColDef,
} from "@mui/x-data-grid";

import ChipFilledOther from "../../../common/components/chip/ChipFilledOther";
import ChipFilledPrimary from "../../../common/components/chip/ChipFilledPrimary";
import RouterLink from "../../../common/components/RouterLink";
import DeleteDeviceContainer from "../components/DeleteDeviceContainer";

import EditIconButton from "../../../common/components/icons/EditIconButton";
import InfoIcon from "../../../common/components/icons/InfoIcon";

import TableCellTruncated from "../../../common/components/table/TableCellTruncated";
import DeviceConnectedModelsColumn from "../components/DeviceConnectedModelsColumn";
import {
  getSKUDisplayLabel,
  isSKUAssigned,
  isLegacySKU,
} from "../../../common/variables/skuOptions";

export const useDeviceTableColumns = (): { columns: GridColDef[] } => {
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "rowId",
        headerName: "#",
        hideable: false,
        flex: 1,
        maxWidth: 50,
        disableColumnMenu: true,
        renderCell: (params): JSX.Element | null =>
          params ? (
            <Typography variant="body2">
              {parseInt(params.row.sequenceNumber)}
            </Typography>
          ) : null,
      },

      {
        field: "skuNumber",
        headerName: "SKU",
        hideable: true,
        sortable: true,
        sortComparator: (a, b) => {
          // ← add after sortable
          if (!a && !b) return 0;

          if (!a) return 1;

          if (!b) return -1;

          return a.localeCompare(b);
        },
        flex: 1,
        minWidth: 120,
        disableColumnMenu: true,
        renderCell: (params): JSX.Element | null => {
          if (!params.row) {
            return null;
          }

          const skuValue = params.row.skuNumber;
          const isAssigned = isSKUAssigned(skuValue);
          const displayLabel = getSKUDisplayLabel(skuValue);

          // Not assigned (null/undefined) - show empty
          if (!isAssigned) {
            return <Typography variant="body2">-</Typography>;
          }

          // Explicitly marked as Legacy
          if (isLegacySKU(skuValue)) {
            return <Typography variant="body2">Legacy</Typography>;
          }

          // Has actual SKU assigned
          return (
            <TableCellTruncated
              tooltipText={displayLabel}
              text={displayLabel}
              TypographyProps={{
                variant: "body2",
              }}
            />
          );
        },
      },
      {
        field: "skuName",
        headerName: "PRODUCT NAME",
        hideable: true,
        sortable: true,
        flex: 1,
        minWidth: 140,
        disableColumnMenu: true,
        renderCell: (params): JSX.Element | null => {
          if (!params.row) {
            return null;
          }

          return (
            <TableCellTruncated
              tooltipText={params.row.skuName || ""}
              text={params.row.skuName || "-"}
              TypographyProps={{
                variant: "body2",
              }}
            />
          );
        },
      },
      {
        field: "serialNumber",
        headerName: "SERIAL NUMBER",
        hideable: true,
        sortable: true,
        flex: 1,
        minWidth: 140,
        disableColumnMenu: true,
        renderCell: (params): JSX.Element | null => {
          if (!params.row) {
            return null;
          }

          return (
            <TableCellTruncated
              tooltipText={params.row.serialNumber || ""}
              text={params.row.serialNumber || "-"}
              TypographyProps={{
                variant: "body2",
              }}
            />
          );
        },
      },
      {
        field: "name",
        headerName: "DEVICE NAME",
        hideable: false,
        flex: 1,
        minWidth: 140,
        disableColumnMenu: true,
        renderCell: (params): JSX.Element | null =>
          params ? (
            <RouterLink
              to={`/device/${encodeURIComponent(params.row.rowId)}`}
              title={params.row.name}
              state={{ deviceDatum: params.row }}
            />
          ) : null,
      },
      {
        field: "cameraIpAddress",
        headerName: "CAMERA IP",
        hideable: false,
        minWidth: 120,
        flex: 1,
        disableColumnMenu: true,
        renderCell: (params): JSX.Element | null =>
          params ? (
            <TableCellTruncated
              tooltipText={""}
              text={params?.row.cameraIpAddress}
              TypographyProps={{
                variant: "body2",
              }}
            />
          ) : null,
      },
      {
        field: "models",
        headerName: "MODEL",
        hideable: false,
        width: 320,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params): JSX.Element | null => {
          return (
            <Box
              onClick={e => e.stopPropagation()}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                padding: "0.5em 0",
                gap: "0.5em",
              }}
            >
              {params?.row && (
                <DeviceConnectedModelsColumn device={params.row} />
              )}
            </Box>
          );
        },
      },
      {
        field: "node",
        headerName: "NODE",
        hideable: false,
        sortable: true,
        sortComparator: (a, b): number => a.localeCompare(b),
        flex: 1,
        minWidth: 140,
        disableColumnMenu: true,
        valueGetter: (node: {
          id: string | null;
          name: string | null;
        }): string => {
          if (!node?.id) {
            return "";
          }

          const nodeName = node?.name ?? node?.id.split("#N#").at(-1);

          return nodeName ?? "";
        },
        renderCell: (params): JSX.Element | null => {
          if (!params.row) {
            return null;
          }

          const node =
            params.row.node.name ?? params.row.node.id.split("#N#").at(-1);

          return <Typography variant="body2">{node}</Typography>;
        },
      },
      {
        field: "isOnline",
        renderHeader: () => {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5em",
              }}
            >
              <Typography sx={{ fontSize: "12px" }}>STATUS</Typography>

              <Tooltip
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
                title="Only models of online devices are monitored"
              >
                <Box>
                  <InfoIcon />
                </Box>
              </Tooltip>
            </Box>
          );
        },
        hideable: false,
        sortable: false,
        flex: 1,
        minWidth: 120,
        disableColumnMenu: true,
        renderCell: (params): JSX.Element | null => {
          if (!params.row) {
            return null;
          }

          const isOnline = params?.row.isOnline;

          return (
            <>
              {isOnline ? (
                <ChipFilledPrimary label="Online" />
              ) : (
                <ChipFilledOther label="Offline" />
              )}
            </>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        width: 100,
        getActions: (params): ReactElement<GridActionsCellItemProps>[] => [
          <GridActionsCellItem
            key={`edit-${params.row?.rowId}`}
            icon={
              <RouterLink
                to={`/device/edit/${encodeURIComponent(params.row.rowId)}`}
                state={{ deviceDatum: params.row, useBackToDevicesPage: true }}
              >
                <EditIconButton />
              </RouterLink>
            }
            label="Edit"
            className="actionButton"
            disableRipple
          />,
          <GridActionsCellItem
            key={`delete-${params.row?.rowId}`}
            icon={<DeleteDeviceContainer row={params.row} />}
            label="Delete"
            className="actionButton"
            disableRipple
          />,
        ],
      },
    ],
    []
  );

  return { columns };
};
